import { captureException } from '@sentry/browser';
import {
  ActivityStage,
  ActivityStatus,
} from '@showbie-socrative/socrative-utils';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import produce from 'immer';
import { normalize } from 'normalizr';
import Pubnub from 'pubnub';
import { useDispatch } from 'react-redux';

import { FinishActivityEventAdapter } from '~analytics/adapters/FinishActivityEventAdapter';
import { PauseActivityEventAdapter } from '~analytics/adapters/PauseActivityEventAdapter';
import { sendPassRateEvent } from '~analytics/index';
import { FinishRoute } from '~analytics/types';
import {
  Activity,
  ApiQuestionResponse,
  QuestionScore,
  Room,
  Student,
  activitySchema,
} from '~teacher/models/activities';
import { currentRoomActivityChanged } from '~teacher/slices/teacher/teacherSlice';

import {
  Activity as ApiActivity,
  finishActivity,
  getActivityById,
  getCurrentActivitiesByRoom,
  toggleActivityStatus,
  updateActivityState,
  updatePassRate,
} from '../../../api/activities/activityApi';
import { isQuizNotFoundError } from '../../../api/isQuizNotFoundError';
import { queryKey } from './queryKey';

export function normalizeActivityResponse(data: ApiActivity[]) {
  const result = normalize<
    ApiActivity,
    {
      activities: Record<string, Activity>;
      students: Record<string, Student>;
      responses: Record<string, ApiQuestionResponse>;
      questionScores: Record<string, QuestionScore>;
      room: Room;
    },
    // the api response could be anything.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >(data, [activitySchema]);
  const {
    entities: {
      activities = {},
      students = {},
      responses = {},
      room = {},
      questionScores = {},
    },
  } = result;

  const [activity] = Object.values<Activity>(activities);
  const [singleRoom] = Object.values<Room>(room);
  return {
    activity,
    students,
    responses,
    room: singleRoom,
    questionScores,
  };
}

export type ActivityResponse = ReturnType<typeof normalizeActivityResponse>;

export async function checkStudentPresence({
  activityData,
  pubnub,
}: {
  activityData: ActivityResponse;
  pubnub: Pubnub;
}) {
  if (
    activityData?.activity?.activityStage === ActivityStage.InProgress &&
    activityData?.room?.isRostered
  ) {
    try {
      const newStudents = { ...activityData.students }; // Make a copy, so that if things fail, we don't potentially send half updated data.
      const pubnubChannels = await pubnub.hereNow({
        channels: [`${activityData.room.nameLower}-student`],
        includeState: true,
        includeUUIDs: true,
      });

      const uuidSet = new Set(
        pubnubChannels.channels[
          `${activityData.room.nameLower}-student`
        ].occupants.map((occupant) => occupant.uuid)
      );

      Object.values<Student>(newStudents).forEach((student) => {
        if (uuidSet.has(student.userUuid)) {
          student.isPresent = true;
        }
      });

      activityData.students = newStudents;
    } catch (error) {
      console.error('Error checking pubnub presence');
      console.error(error);
      captureException(error);
    }
  }
}

export const getActivity = async (activityId: number, pubnub?: Pubnub) => {
  const { data } = await getActivityById(activityId);
  const activityData = normalizeActivityResponse([data]);
  if (pubnub) {
    await checkStudentPresence({ activityData: activityData, pubnub: pubnub });
  }
  return activityData;
};

export const getActivityByRoomId = async (roomId: number, pubnub: Pubnub) => {
  const { data } = await getCurrentActivitiesByRoom(roomId);
  const activityData = normalizeActivityResponse(data[0] ? [data[0]] : []);
  await checkStudentPresence({ activityData: activityData, pubnub: pubnub });
  return activityData;
};

export function getActivityByInstanceIdOptions({
  activityInstanceId,
  pubnub,
  onError,
}: {
  activityInstanceId: number;
  pubnub?: Pubnub;
  onError?: (error: Error) => void;
}) {
  return {
    queryKey: queryKey.byId(activityInstanceId),
    queryFn: () => getActivity(activityInstanceId, pubnub),
    enabled: !!activityInstanceId,
    onError: onError,
    // A quiz_not_found (deleted/inaccessible library) won't recover, so surface
    // it immediately instead of burning the default retries.
    retry: (failureCount: number, error: unknown) =>
      !isQuizNotFoundError(error) && failureCount <= 3,
  };
}

interface UseActivityByInstanceIdParams {
  activityInstanceId: number;
  pubnub?: Pubnub;
  onError?: (error: Error) => void;
}

function useActivityByInstanceId({
  activityInstanceId,
  pubnub,
  onError = (error: Error) => {
    captureException(error);
  },
}: UseActivityByInstanceIdParams) {
  return useQuery(
    getActivityByInstanceIdOptions({
      activityInstanceId,
      pubnub,
      onError,
    })
  );
}

function useActivityByCurrentRoom(currentRoomId: number, pubnub: Pubnub) {
  const queryCache = useQueryClient();

  return useQuery({
    queryKey: queryKey.byRoomId(currentRoomId),
    queryFn: () => getActivityByRoomId(currentRoomId, pubnub),
    enabled: !!currentRoomId,
    onSuccess: (data) => {
      const activityInstanceId = data.activity?.id;
      if (activityInstanceId) {
        queryCache.setQueryData(queryKey.byId(activityInstanceId), data);
      }
    },
    onError: (error: Error) => {
      captureException(error);
    },
    staleTime: 1000,
  });
}
const activityStateUpdater = ({ activityState }: { activityState: number }) =>
  produce((activityData: ActivityResponse) => {
    activityData.activity.activityState = activityState;
    return activityData;
  });

const activityPassRateUpdater = ({ passRate }: { passRate: number }) =>
  produce((activityData: ActivityResponse) => {
    activityData.activity.passRate = passRate;
    return activityData;
  });

const activityStatusCacheUpdater = ({
  queryClient,
  activityStatus,
  activityInstanceId,
}: {
  queryClient: QueryClient;
  activityStatus: ActivityStatus;
  activityInstanceId: number;
}) => {
  queryClient.setQueryData(
    queryKey.byId(activityInstanceId),
    produce((activityData: ActivityResponse) => {
      activityData.activity.state = activityStatus;

      if (activityStatus === ActivityStatus.Finished) {
        // Re-fetch activity data for the report stats
        queryClient.refetchQueries(queryKey.byId(activityInstanceId));
      }

      return activityData;
    })
  );
};

function useUpdateActivityState() {
  const queryCache = useQueryClient();
  return useMutation(
    ({
      activityInstanceId,
      questionPosition,
    }: {
      activityInstanceId: number;
      questionPosition: number;
    }) => {
      return updateActivityState(activityInstanceId, questionPosition);
    },
    {
      onSuccess: (results, { activityInstanceId }) => {
        const { data } = results;
        const { activityState } = data;
        queryCache.setQueryData(
          queryKey.byId(activityInstanceId),
          activityStateUpdater({ activityState: activityState })
        );
      },
      onError: (error) => {
        captureException(error);
      },
    }
  );
}

function useToggleActivityStatus() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ activityInstanceId }: { activityInstanceId: number }) => {
      return toggleActivityStatus(activityInstanceId);
    },
    {
      onSuccess: async (results, { activityInstanceId }) => {
        const { state } = results.data;
        activityStatusCacheUpdater({
          queryClient: queryClient,
          activityStatus: state,
          activityInstanceId: activityInstanceId,
        });

        const activityData = queryClient.getQueryData<ActivityResponse>(
          queryKey.byId(activityInstanceId)
        );

        // Analytics
        new PauseActivityEventAdapter({
          isRosteredRoom: activityData.room.isRostered,
          settings: activityData.activity.settings,
          activityType: results.data.activityType,
          numberStudents: Object.keys(activityData.students).length,
          numberResponses: Object.keys(activityData.responses).length,
          startTime: results.data.startTime,
          actionType: state === ActivityStatus.Paused ? 'pause' : 'resume',
        }).sendAnalytics();

        // Refetch timer states — server bulk-pauses or bulk-resumes all timers
        queryClient.invalidateQueries([
          'activity',
          'timers',
          activityInstanceId,
        ]);
        await queryClient.invalidateQueries(['activity-by-room']);
      },
      onError: async (error, { activityInstanceId }) => {
        await queryClient.invalidateQueries(queryKey.byId(activityInstanceId));
        captureException(error);
      },
    }
  );
}

function useFinishActivity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation(
    ({
      activityInstanceId,
    }: {
      activityInstanceId: number;
      finishRoute: FinishRoute;
    }) => {
      return finishActivity(activityInstanceId);
    },
    {
      onSuccess: (results, { activityInstanceId, finishRoute }) => {
        const newActivity = normalizeActivityResponse([results.data]);
        queryClient.setQueryData(
          queryKey.byId(activityInstanceId),
          function () {
            return newActivity;
          }
        );

        // Refetch timer states — server expires all timers on finish
        queryClient.invalidateQueries([
          'activity',
          'timers',
          activityInstanceId,
        ]);
        queryClient.invalidateQueries([
          queryKey.byRoomId(results.data.room.id),
        ]);

        // Analytics
        new FinishActivityEventAdapter({
          isRosteredRoom: results.data['isRostered'],
          settings: results.data['settings'],
          activityType: results.data['activityType'],
          numberStudents: results.data['students'].length,
          numberResponses: results.data['responses'].length,
          startTime: results.data['startTime'],
          endTime: results.data['endTime'],
          finishRoute: finishRoute,
        }).sendAnalytics();

        dispatch(
          currentRoomActivityChanged({
            activityInstanceId: activityInstanceId,
            isLive: false,
          })
        );
      },
      onError: (error) => {
        captureException(error);
      },
    }
  );
}

function useUpdatePassRate() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      activityInstanceId,
      passRate,
    }: {
      activityInstanceId: number;
      passRate: number | null;
    }) => {
      return updatePassRate(activityInstanceId, passRate);
    },
    {
      onSuccess: async (results, { activityInstanceId }) => {
        // Optimistically update the passRate in cache
        queryClient.setQueryData(
          queryKey.byId(activityInstanceId),
          activityPassRateUpdater({ passRate: results.data.passRate })
        );

        // Invalidate and refetch to get updated passing_students_rate from service
        await queryClient.invalidateQueries(queryKey.byId(activityInstanceId));

        // send analytics event
        sendPassRateEvent({
          action: 'save',
          value: results.data.passRate,
        });
      },
      onError: (error) => {
        captureException(error);
      },
    }
  );
}

export {
  useActivityByInstanceId,
  useActivityByCurrentRoom,
  useUpdateActivityState,
  useToggleActivityStatus,
  useFinishActivity,
  useUpdatePassRate,
  activityStatusCacheUpdater,
};
